import * as THREE from 'three';

// Shared GLSL helpers: value noise and HSV conversion.
export const inkNoiseGLSL = /* glsl */ `
  float inkHash(vec3 p) {
    p = fract(p * 0.3183099 + 0.1);
    p *= 17.0;
    return fract(p.x * p.y * p.z * (p.x + p.y + p.z));
  }
  float inkNoise(vec3 x) {
    vec3 i = floor(x);
    vec3 f = fract(x);
    f = f * f * (3.0 - 2.0 * f);
    return mix(mix(mix(inkHash(i + vec3(0, 0, 0)), inkHash(i + vec3(1, 0, 0)), f.x),
                   mix(inkHash(i + vec3(0, 1, 0)), inkHash(i + vec3(1, 1, 0)), f.x), f.y),
               mix(mix(inkHash(i + vec3(0, 0, 1)), inkHash(i + vec3(1, 0, 1)), f.x),
                   mix(inkHash(i + vec3(0, 1, 1)), inkHash(i + vec3(1, 1, 1)), f.x), f.y), f.z);
  }
  vec3 inkRgb2hsv(vec3 c) {
    vec4 K = vec4(0.0, -1.0 / 3.0, 2.0 / 3.0, -1.0);
    vec4 p = mix(vec4(c.bg, K.wz), vec4(c.gb, K.xy), step(c.b, c.g));
    vec4 q = mix(vec4(p.xyw, c.r), vec4(c.r, p.yzx), step(p.x, c.r));
    float d = q.x - min(q.w, q.y);
    return vec3(abs(q.z + (q.w - q.y) / (6.0 * d + 1e-10)), d / (q.x + 1e-10), q.x);
  }
  vec3 inkHsv2rgb(vec3 c) {
    vec3 p = abs(fract(c.xxx + vec3(1.0, 2.0 / 3.0, 1.0 / 3.0)) * 6.0 - 3.0);
    return c.z * mix(vec3(1.0), clamp(p - 1.0, 0.0, 1.0), c.y);
  }
  vec2 inkEncodeNormal(vec3 n) {
    float f = sqrt(8.0 * n.z + 8.0);
    return n.xy / f + 0.5;
  }
`;

const vertexShader = /* glsl */ `
  #include <common>
  #include <uv_pars_vertex>
  #include <color_pars_vertex>
  #include <skinning_pars_vertex>
  #include <shadowmap_pars_vertex>
  varying vec3 vViewNormal;
  varying vec3 vWorldPos;
  void main() {
    #include <uv_vertex>
    #include <color_vertex>
    #include <beginnormal_vertex>
    #include <skinbase_vertex>
    #include <skinnormal_vertex>
    #include <defaultnormal_vertex>
    #include <begin_vertex>
    #include <skinning_vertex>
    #include <project_vertex>
    #include <worldpos_vertex>
    #include <shadowmap_vertex>
    vViewNormal = normalize(transformedNormal);
    vec4 wp = vec4(transformed, 1.0);
    #ifdef USE_INSTANCING
      wp = instanceMatrix * wp;
    #endif
    vWorldPos = (modelMatrix * wp).xyz;
  }
`;

const fragmentShader = /* glsl */ `
  layout(location = 1) out highp vec4 gInfo;
  #include <common>
  #include <packing>
  #include <color_pars_fragment>
  #include <uv_pars_fragment>
  #include <map_pars_fragment>
  #include <lights_pars_begin>
  #include <shadowmap_pars_fragment>
  uniform vec3 uColor;
  uniform float uSurfaceId;
  uniform vec2 uBand;
  uniform float uShadowHue;
  uniform float uShadowValue;
  uniform float uLineBreak;
  uniform float uLineScale;
  uniform float uTone;
  uniform vec3 uTint;
  uniform float uDesat;
  uniform float uFlash;
  varying vec3 vViewNormal;
  varying vec3 vWorldPos;
  ${inkNoiseGLSL}
  void main() {
    vec3 base = uColor;
    #ifdef USE_COLOR
      base *= vColor.rgb;
    #endif
    #ifdef USE_MAP
      base *= texture2D(map, vMapUv).rgb;
    #endif
    // Per-object recolour: desaturate then tint (corporate greys), plus a white hit-flash.
    base = mix(base, vec3(dot(base, vec3(0.299, 0.587, 0.114))), uDesat) * uTint;
    vec3 N = normalize(vViewNormal);
    if (!gl_FrontFacing) N = -N;

    float lit = 1.0;
    #if NUM_DIR_LIGHTS > 0
      lit = max(dot(N, directionalLights[0].direction), 0.0);
      #if defined(USE_SHADOWMAP) && NUM_DIR_LIGHT_SHADOWS > 0
        DirectionalLightShadow s = directionalLightShadows[0];
        lit *= getShadow(directionalShadowMap[0], s.shadowMapSize, s.shadowIntensity, s.shadowBias, s.shadowRadius, vDirectionalShadowCoord[0]);
      #endif
    #endif

    // Two tones only: lit colour, and a darker, slightly hue-shifted copy of it.
    float band = smoothstep(uBand.x, uBand.y, lit);
    vec3 hsv = inkRgb2hsv(base);
    vec3 shade = inkHsv2rgb(vec3(fract(hsv.x + uShadowHue), clamp(hsv.y * 1.12, 0.0, 1.0), hsv.z * uShadowValue));
    vec3 col = mix(shade, base, band);

    // Stepped tonal patches so big flat areas don't look like plastic.
    float patches = step(0.55, inkNoise(vWorldPos * 1.7));
    col *= 1.0 + (patches - 0.5) * uTone;

    // Outline mask: noise knocks holes in the lines so they read hand-inked.
    float lineMask = step(uLineBreak, inkNoise(vWorldPos * uLineScale + 7.31));

    col = mix(col, vec3(1.0), uFlash);
    gl_FragColor = vec4(col, uSurfaceId);
    gInfo = vec4(inkEncodeNormal(N), lineMask, 1.0);
  }
`;

export type InkMaterialOptions = {
  color?: THREE.ColorRepresentation;
  vertexColors?: boolean;
  /** Smoothstep range on light*shadow; a tight range gives a hard cut. */
  band?: [number, number];
  shadowHue?: number;
  shadowValue?: number;
  /** 0 = solid lines, ~0.4 = sketchy broken lines. */
  lineBreak?: number;
  lineScale?: number;
  tone?: number;
  side?: THREE.Side;
  /** Base colour texture (e.g. a character's palette atlas). */
  map?: THREE.Texture | null;
};

let nextSurface = 0;
/** Distinct-ish ids in (0,1); outlines appear wherever ids change. */
export function newSurfaceId(): number {
  nextSurface += 1;
  return 0.05 + ((nextSurface * 0.61803398875) % 1) * 0.9;
}

export function createInkMaterial(opts: InkMaterialOptions = {}): THREE.ShaderMaterial {
  const uniforms = THREE.UniformsUtils.merge([
    THREE.UniformsLib.lights,
    {
      uColor: { value: new THREE.Color(opts.color ?? 0xffffff) },
      uSurfaceId: { value: newSurfaceId() },
      uBand: { value: new THREE.Vector2(...(opts.band ?? [0.18, 0.32])) },
      uShadowHue: { value: opts.shadowHue ?? -0.025 },
      uShadowValue: { value: opts.shadowValue ?? 0.58 },
      uLineBreak: { value: opts.lineBreak ?? 0.32 },
      uLineScale: { value: opts.lineScale ?? 9 },
      uTone: { value: opts.tone ?? 0.05 },
      map: { value: opts.map ?? null },
      uTint: { value: new THREE.Color(1, 1, 1) },
      uDesat: { value: 0 },
      uFlash: { value: 0 },
      mapTransform: { value: new THREE.Matrix3() },
    },
  ]);
  const mat = new THREE.ShaderMaterial({
    uniforms,
    vertexShader,
    fragmentShader,
    lights: true,
    vertexColors: opts.vertexColors ?? false,
    side: opts.side ?? THREE.FrontSide,
  });
  if (opts.map) {
    // three keys USE_MAP / vMapUv off the material's own `map` property.
    (mat as unknown as { map: THREE.Texture }).map = opts.map;
    uniforms.map.value = opts.map;
    if (opts.map.matrixAutoUpdate) opts.map.updateMatrix();
    uniforms.mapTransform.value.copy(opts.map.matrix);
  }
  return mat;
}

const flatVert = /* glsl */ `
  #include <common>
  varying vec3 vViewNormal;
  void main() {
    #include <beginnormal_vertex>
    #include <defaultnormal_vertex>
    #include <begin_vertex>
    #include <project_vertex>
    vViewNormal = normalize(transformedNormal);
  }
`;
const flatFrag = /* glsl */ `
  layout(location = 1) out highp vec4 gInfo;
  uniform vec3 uColor;
  uniform float uSurfaceId;
  uniform float uLine;
  uniform float uOpacity;
  varying vec3 vViewNormal;
  ${inkNoiseGLSL}
  void main() {
    gl_FragColor = vec4(uColor, uSurfaceId);
    gInfo = vec4(inkEncodeNormal(normalize(vViewNormal)), uLine, 1.0);
  }
`;

/**
 * Unlit colour that still writes the outline buffers. Use for eyes, flames,
 * lit windows. `line` 1 draws ink around it, 0 leaves it clean (glows).
 */
export function createFlatMaterial(color: THREE.ColorRepresentation, line = 0): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    vertexShader: flatVert,
    fragmentShader: flatFrag,
    uniforms: {
      uColor: { value: new THREE.Color(color) },
      uSurfaceId: { value: newSurfaceId() },
      uLine: { value: line },
      uOpacity: { value: 1 },
    },
  });
}

/** Give a mesh (or every mesh in a group) its own outline id. */
export function assignSurfaceIds(root: THREE.Object3D): void {
  root.traverse((o) => {
    const m = (o as THREE.Mesh).material as THREE.ShaderMaterial | undefined;
    if (m?.uniforms?.uSurfaceId) {
      (o as THREE.Mesh).material = m.clone();
      ((o as THREE.Mesh).material as THREE.ShaderMaterial).uniforms.uSurfaceId.value = newSurfaceId();
    }
  });
}
